import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ChatBubble as ChatBubbleIcon,
} from '@mui/icons-material';
import { useChatStore } from '@/store/chatStore';
import { fetchConversations, archiveConversation, unarchiveConversation } from '@/api/chat';
import type { ChatConversation } from '@/types/chat';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
// ArchiveIcon/UnarchiveIcon still used inside the list rows below.

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}

export default function ChatPage() {
  const navigate = useNavigate();
  const { conversations, setConversations, updateConversationStatus } = useChatStore();
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchConversations(tab)
      .then((convs) => {
        if (cancelled) return;
        setConversations(convs);
        setError(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, setConversations]);

  const handleTabChange = (next: 'active' | 'archived') => {
    setLoading(true);
    setTab(next);
  };

  const handleArchive = async (e: { stopPropagation: () => void }, conv: ChatConversation) => {
    e.stopPropagation();
    try {
      await archiveConversation(conv.conversationId);
      updateConversationStatus(conv.conversationId, 'archived');
    } catch {}
  };

  const handleUnarchive = async (e: { stopPropagation: () => void }, conv: ChatConversation) => {
    e.stopPropagation();
    try {
      await unarchiveConversation(conv.conversationId);
      updateConversationStatus(conv.conversationId, 'active');
    } catch {}
  };

  const filtered = conversations.filter((c) => c.status === tab);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        icon={<ChatBubbleIcon />}
        title="Chat"
        subtitle="Conversaciones bidireccionales con tus agentes."
      />

      <Tabs
        value={tab}
        onChange={(_, v) => handleTabChange(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Activos" value="active" />
        <Tab label="Archivados" value="archived" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={<ChatBubbleIcon />}
          title={`No hay conversaciones ${tab === 'archived' ? 'archivadas' : 'activas'}`}
          description={
            tab === 'active'
              ? 'Cuando un agente te escriba, la conversación aparecerá acá.'
              : 'Las conversaciones que archives aparecerán en este listado.'
          }
        />
      )}

      {!loading && filtered.length > 0 && (
        <Paper variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
          <List disablePadding>
            {filtered.map((conv, idx) => (
              <Box key={conv.conversationId}>
                {idx > 0 && <Divider component="li" />}
                <ListItemButton
                  onClick={() => navigate(`/chat/${conv.agentId}`)}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <ComputerIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={conv.unreadCount > 0 ? 700 : 400} noWrap>
                          {conv.agentHostname || conv.agentId}
                        </Typography>
                        {conv.unreadCount > 0 && (
                          <Chip
                            label={conv.unreadCount}
                            size="small"
                            color="error"
                            sx={{
                              height: 18,
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block', maxWidth: 400 }}
                      >
                        {conv.lastMessage
                          ? `${conv.lastMessage.senderType === 'admin' ? 'Tú: ' : ''}${conv.lastMessage.content}`
                          : 'Sin mensajes'}
                      </Typography>
                    }
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.5,
                      ml: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {relativeTime(conv.lastMessageAt ?? conv.createdAt)}
                    </Typography>
                    {tab === 'active' ? (
                      <Tooltip title="Archivar">
                        <IconButton size="small" onClick={(e) => handleArchive(e, conv)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Desarchivar">
                        <IconButton size="small" onClick={(e) => handleUnarchive(e, conv)}>
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
